import { useState } from "react";
import type { ReactNode } from "react";
import { Navigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Languages, LogOut, Mail, UserRound } from "lucide-react";

import { CenteredLoader, PageIntro } from "@/components/common";
import { useAppSignOut } from "@/hooks/use-app-sign-out";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";
import { getUserDisplayName, getUserInitials } from "@/lib/user";
import { trpc } from "@/lib/trpc";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@runcheetah/ui";

type SaveState = "idle" | "saving" | "saved" | "error";

export default function AccountProfileSettingsPage() {
  const intl = useIntl();
  const { user, isPending } = useAuthenticatedUser();
  const [languageState, setLanguageState] = useState<SaveState>("idle");
  const utils = trpc.useUtils();
  const handleSignOut = useAppSignOut();
  const settingsQuery = trpc.settings.get.useQuery(undefined, {
    enabled: Boolean(user),
  });
  const updateSettings = trpc.settings.update.useMutation({
    onSuccess: () => utils.settings.get.invalidate(),
  });

  if (isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "accountProfile.loading",
          defaultMessage: "Loading profile...",
        })}
      />
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const displayName = getUserDisplayName(user);
  const initials = getUserInitials(user);
  const activeLocale: SupportedLocale = settingsQuery.data?.locale ?? "en";
  const localeDisabled = settingsQuery.isPending || updateSettings.isPending;

  const handleLocaleChange = async (nextLocale: SupportedLocale) => {
    setLanguageState("saving");

    try {
      await updateSettings.mutateAsync({ locale: nextLocale });
      setLanguageState("saved");
    } catch {
      setLanguageState("error");
    }
  };

  return (
    <div className="w-full space-y-6 px-6 py-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <PageIntro
          title={intl.formatMessage({
            id: "accountProfile.title",
            defaultMessage: "Profile",
          })}
          description={intl.formatMessage({
            id: "accountProfile.description",
            defaultMessage: "Review the Google account connected to Cheetah.",
          })}
        />

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <Card className="overflow-hidden border-0 bg-card/95 shadow-sm">
            <CardHeader className="bg-muted/20 pb-4">
              <div className="flex items-center gap-4">
                <Avatar className="size-14 rounded-2xl border border-border">
                  {user.image ? (
                    <AvatarImage src={user.image} alt={displayName} referrerPolicy="no-referrer" />
                  ) : null}
                  <AvatarFallback className="rounded-2xl bg-primary text-base font-semibold text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <CardTitle className="truncate text-xl">{displayName}</CardTitle>
                  <p className="truncate text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="divide-y divide-border p-0">
              <ProfileRow
                icon={<UserRound className="size-4" />}
                label={intl.formatMessage({
                  id: "accountProfile.nameLabel",
                  defaultMessage: "Name",
                })}
                value={user.name}
              />
              <ProfileRow
                icon={<Mail className="size-4" />}
                label={intl.formatMessage({
                  id: "accountProfile.emailLabel",
                  defaultMessage: "Email",
                })}
                value={user.email}
              />
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="border-0 bg-card/95 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Languages className="size-4 text-muted-foreground" />
                  <FormattedMessage id="accountProfile.languageTitle" defaultMessage="Language" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm leading-6 text-muted-foreground">
                  <FormattedMessage
                    id="accountProfile.languageDescription"
                    defaultMessage="Choose the interface and default content language for your account."
                  />
                </p>
                <Select
                  value={activeLocale}
                  onValueChange={(value) => void handleLocaleChange(value as SupportedLocale)}
                  disabled={localeDisabled}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={intl.formatMessage({
                        id: "accountProfile.selectLanguage",
                        defaultMessage: "Select language",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LOCALES.map((locale) => (
                      <SelectItem key={locale} value={locale}>
                        {LOCALE_LABELS[locale]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {languageState !== "idle" ? (
                  <p className="text-xs text-muted-foreground">
                    {languageState === "saving" ? (
                      <FormattedMessage id="common.saving" defaultMessage="Saving..." />
                    ) : null}
                    {languageState === "saved" ? (
                      <FormattedMessage id="common.saved" defaultMessage="Saved" />
                    ) : null}
                    {languageState === "error" ? (
                      <FormattedMessage
                        id="common.saveErrorTryAgain"
                        defaultMessage="Could not save. Try again."
                      />
                    ) : null}
                  </p>
                ) : null}
              </CardContent>
            </Card>

            <Card className="border-0 bg-card/95 shadow-sm">
              <CardContent className="space-y-3 p-5">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    <FormattedMessage id="accountProfile.sessionTitle" defaultMessage="Session" />
                  </p>
                  <p className="text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="accountProfile.sessionDescription"
                      defaultMessage="Sign out of this browser when you are done."
                    />
                  </p>
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  onClick={() => void handleSignOut()}
                >
                  <LogOut className="size-4" />
                  <FormattedMessage id="sidebar.signOut" defaultMessage="Sign out" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="grid gap-3 px-5 py-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-center">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="min-w-0 truncate text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}
